// Shared seasonal palette logic — single source of truth for date -> season -> accent-family
// -> monthly hue/lightness shift. Used by ThemeContext (app-wide --accent variables) and
// TreeShelf (tree/leaf rendering) so both stay in sync instead of drifting apart.
//
// Accent-family naming matches the existing app-wide accent system (ThemeContext's
// `accent` state / `data-accent` attribute): forest, ocean, sunset, slate.

export const MONTH_PALETTES = {
  2: { season: 'spring', accentFamily: 'forest', label: 'Spring Bud',     hue: 95,  sat: 45, lightDark: 65, lightLight: 45 },
  3: { season: 'spring', accentFamily: 'forest', label: 'Spring Leaf',    hue: 122, sat: 39, lightDark: 60, lightLight: 49 },
  4: { season: 'spring', accentFamily: 'forest', label: 'Spring Canopy',  hue: 145, sat: 40, lightDark: 55, lightLight: 40 },
  5: { season: 'summer', accentFamily: 'ocean',  label: 'Sky Ocean',      hue: 195, sat: 85, lightDark: 65, lightLight: 50 },
  6: { season: 'summer', accentFamily: 'ocean',  label: 'Mid Ocean',      hue: 207, sat: 90, lightDark: 65, lightLight: 54 },
  7: { season: 'summer', accentFamily: 'ocean',  label: 'Deep Teal Ocean',hue: 218, sat: 85, lightDark: 60, lightLight: 46 },
  8: { season: 'autumn', accentFamily: 'sunset', label: 'Golden Sunset',  hue: 42,  sat: 95, lightDark: 60, lightLight: 48 },
  9: { season: 'autumn', accentFamily: 'sunset', label: 'Mid Sunset',     hue: 36,  sat: 100,lightDark: 62, lightLight: 50 },
  10:{ season: 'autumn', accentFamily: 'sunset', label: 'Rust Sunset',    hue: 22,  sat: 90, lightDark: 55, lightLight: 42 },
  11:{ season: 'winter', accentFamily: 'slate',  label: 'Slate Grey Winter', hue: 200, sat: 12, lightDark: 65, lightLight: 46 },
  0: { season: 'winter', accentFamily: 'slate',  label: 'Cool Slate Blue Winter', hue: 205, sat: 18, lightDark: 62, lightLight: 44 },
  1: { season: 'winter', accentFamily: 'slate',  label: 'Silver Slate Winter', hue: 200, sat: 8,  lightDark: 68, lightLight: 50 },
};

export const getMonthPalette = (date = new Date()) => MONTH_PALETTES[date.getMonth()];

// App-wide accent color (used by ThemeContext for --accent / --accent-soft).
export const getSeasonalAccent = (date = new Date(), isDark = false) => {
  const p = getMonthPalette(date);
  const light = isDark ? p.lightDark : p.lightLight;
  const softLight = isDark ? 15 : 94;
  const softSat = isDark ? Math.min(p.sat, 45) : p.sat;
  return {
    season: p.season,
    accentFamily: p.accentFamily,
    label: p.label,
    color: `hsl(${p.hue}, ${p.sat}%, ${light}%)`,
    softColor: `hsl(${p.hue}, ${softSat}%, ${softLight}%)`,
  };
};

// Tree-specific palette: derives a leaf color set, trunk color, background, and seasonal
// decoration flags (blossom / bare-branches / snow) from the same month->hue mapping, so
// the tree visibly follows the real season and drifts monthly within each season.
export const getTreePalette = (date = new Date()) => {
  const p = getMonthPalette(date);
  const { season, hue, sat } = p;

  // 5-shade leaf family fanned around the month's base hue, so leaves still read as
  // "varied foliage" (per-book hash picks among these) while staying on-season.
  const leafHueSpread = season === 'autumn' ? 18 : 12;
  const leafColors = [-2, -1, 0, 1, 2].map((i) => {
    const h = (hue + i * leafHueSpread + 360) % 360;
    const l = season === 'winter' ? 78 + i * 2 : 42 + i * 4;
    const s = season === 'winter' ? Math.max(sat, 15) : sat;
    return `hsl(${h}, ${s}%, ${l}%)`;
  });

  const blossomColors = ['hsl(340, 70%, 88%)', 'hsl(330, 80%, 82%)', 'hsl(350, 60%, 92%)'];

  const trunkColor = season === 'winter' ? '#6d5f57' : '#5D4037';

  const backgroundBySeason = {
    spring: { bg: '#f1f8e9', border: '#c5e1a5' },
    summer: { bg: '#e8f5f0', border: '#a5d6c5' },
    autumn: { bg: '#fdf1e3', border: '#e8c39e' },
    winter: { bg: '#eef2f5', border: '#c8d3da' },
  };

  return {
    season,
    accentFamily: p.accentFamily,
    label: p.label,
    leafColors,
    blossomColors,
    trunkColor,
    background: backgroundBySeason[season],
    showBlossoms: season === 'spring',
    showSnow: season === 'winter',
    bareBranches: season === 'winter',
    leafOpacity: season === 'winter' ? 0.55 : 1,
  };
};
